import Phaser from "phaser";
import {
  ArtPipeline,
  facingDirFromAim,
  type AnimState,
} from "../../../art";
import {
  dropfrontCatalog,
  hexToColor,
  type DropPreviewKind,
} from "../../../data/dropfront-catalog";
import { VfxPlayer } from "../../../vfx";
import {
  UnitRig,
  createHpBar,
  dropfrontGripPack,
  updateHpBar,
} from "../../../view/unit-rig";
import type {
  AllyPiece,
  DropfrontState,
  EnemyAgent,
  PendingAttack,
  SimEvent,
  StructureToken,
} from "../dropfront-rules";
import { previewDropCell, unitWorld } from "../dropfront-rules";
import { DfEvent, DfPhase } from "../dropfront-events";
import {
  attackProgress,
  hitImpactAngle,
  lerp2,
  resolveAttackFx,
  resolveHitPreset,
} from "./attack-vfx";

const ART_PACK = "dropfront";
/** Match FFA crowd.walkEnter — Dropfront has no crowd table. */
const WALK_ENTER = 22;

function usesCombatRig(spriteId: string): boolean {
  return (
    spriteId === "assault" ||
    spriteId === "shooter" ||
    spriteId === "engineer" ||
    spriteId === "swarm"
  );
}

function facingFromAim(aimAngle: number): 1 | -1 {
  return Math.cos(aimAngle) >= 0 ? 1 : -1;
}

type EntityView = {
  root: Phaser.GameObjects.Container;
  body: Phaser.GameObjects.Image;
  rig: UnitRig | null;
  hpBg: Phaser.GameObjects.Rectangle;
  hpGhost: Phaser.GameObjects.Rectangle;
  hpFill: Phaser.GameObjects.Rectangle;
  stackText: Phaser.GameObjects.Text;
  spriteId: string;
  affiliation: string;
  scaleMul: number;
  baseSize: number;
  lastKey: string;
  animState: AnimState;
  animTime: number;
  facing: 1 | -1;
  aimAngle: number;
  dying: boolean;
  deathDuration: number;
  bobPhase: number;
};

export type BoardMetrics = {
  originX: number;
  originY: number;
  cellW: number;
  cellH: number;
};

export class DropfrontPresentation {
  private readonly scene: Phaser.Scene;
  private readonly vfx: VfxPlayer;
  private readonly views = new Map<string, EntityView>();
  private boardGfx!: Phaser.GameObjects.Graphics;
  private boardTiles: Phaser.GameObjects.Image[] = [];
  private highlightGfx!: Phaser.GameObjects.Graphics;
  private synergyGfx!: Phaser.GameObjects.Graphics;
  private tracerGfx!: Phaser.GameObjects.Graphics;
  private metrics: BoardMetrics = {
    originX: 0,
    originY: 0,
    cellW: 48,
    cellH: 48,
  };
  private hitBudget = 10;
  private shotBudget = 12;
  private trailBudget = { left: 8 };
  private hoverCol: number | null = null;
  private hoverRow: number | null = null;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.vfx = new VfxPlayer(scene);
    this.boardGfx = scene.add.graphics().setDepth(0);
    this.highlightGfx = scene.add.graphics().setDepth(1);
    this.synergyGfx = scene.add.graphics().setDepth(8);
    this.tracerGfx = scene.add.graphics().setDepth(20);
  }

  setMetrics(m: BoardMetrics): void {
    this.metrics = m;
  }

  worldToScreen(x: number, y: number): { x: number; y: number } {
    return {
      x: this.metrics.originX + x * this.metrics.cellW,
      y: this.metrics.originY + y * this.metrics.cellH,
    };
  }

  cellToScreen(col: number, row: number): { x: number; y: number } {
    return this.worldToScreen(col + 0.5, row + 0.5);
  }

  destroy(): void {
    for (const v of this.views.values()) {
      v.root.destroy(true);
    }
    this.views.clear();
    this.boardGfx.destroy();
    for (const t of this.boardTiles) t.destroy();
    this.boardTiles = [];
    this.highlightGfx.destroy();
    this.synergyGfx.destroy();
    this.tracerGfx.destroy();
  }

  drawBoard(state: DropfrontState): void {
    const level = dropfrontCatalog.level;
    const { colors, boardTiles: tileLook } = dropfrontCatalog.view;
    const g = this.boardGfx;
    g.clear();
    for (const t of this.boardTiles) t.destroy();
    this.boardTiles = [];
    const { originX, originY, cellW, cellH } = this.metrics;
    const hazardSet = new Set(
      level.hazardCells.map((c) => `${c.col},${c.row}`),
    );
    const isHaz = (c: number, r: number) => hazardSet.has(`${c},${r}`);
    // Soft backdrop under tiles (units sit above; tiles must not compete)
    g.fillStyle(hexToColor(colors.bg), 1);
    g.fillRect(
      originX - 2,
      originY - 2,
      level.cols * cellW + 4,
      level.rows * cellH + 4,
    );
    g.fillStyle(hexToColor(colors.cell), 0.55);
    g.fillRect(
      originX,
      originY,
      level.cols * cellW,
      level.rows * cellH,
    );

    for (let r = 0; r < level.rows; r++) {
      for (let c = 0; c < level.cols; c++) {
        const x = originX + c * cellW + cellW * 0.5;
        const y = originY + r * cellH + cellH * 0.5;
        let spriteId = (c + r) % 2 === 0 ? "tile_floor" : "tile_floor_alt";
        let tint = tileLook.floorTint;
        // Slight checker via alpha only — no bright texel noise
        let alpha =
          (c + r) % 2 === 0
            ? tileLook.floorAlpha
            : tileLook.floorAlpha * 0.88;
        if (isHaz(c, r)) {
          spriteId = "tile_rift";
          tint = tileLook.riftTint;
          alpha = tileLook.riftAlpha;
        } else if (
          isHaz(c - 1, r) ||
          isHaz(c + 1, r) ||
          isHaz(c, r - 1) ||
          isHaz(c, r + 1)
        ) {
          spriteId = "tile_rift_edge";
          tint = tileLook.edgeTint;
          alpha = tileLook.edgeAlpha;
        }
        const { key } = ArtPipeline.ensureAnimFrame(
          this.scene,
          ART_PACK,
          spriteId,
          "neutral",
          "idle",
          0,
        );
        const img = this.scene.add
          .image(x, y, key)
          .setDisplaySize(cellW - 1, cellH - 1)
          .setTint(hexToColor(tint))
          .setAlpha(alpha)
          .setDepth(0);
        this.boardTiles.push(img);
      }
    }
  }

  /** Pointer hover cell only (not full-board wash). */
  setHoverCell(col: number | null, row: number | null): void {
    this.hoverCol = col;
    this.hoverRow = row;
  }

  refreshHighlights(state: DropfrontState): void {
    const g = this.highlightGfx;
    g.clear();
    if (!state.selectedCard || state.phase !== DfPhase.Playing) return;
    if (this.hoverCol == null || this.hoverRow == null) return;
    const col = this.hoverCol;
    const row = this.hoverRow;
    const kind = previewDropCell(state, col, row);
    const { colors, fx } = dropfrontCatalog.view;
    const { originX, originY, cellW, cellH } = this.metrics;
    const colorOf = (k: DropPreviewKind): { c: number; a: number } => {
      switch (k) {
        case "synergy":
          return {
            c: hexToColor(colors.highlightSynergy),
            a: fx.highlightSynergyAlpha,
          };
        case "solo":
          return {
            c: hexToColor(colors.highlightSolo),
            a: fx.highlightSoloAlpha,
          };
        case "stack":
          return {
            c: hexToColor(colors.highlightStack),
            a: fx.highlightStackAlpha,
          };
        case "danger":
          return {
            c: hexToColor(colors.highlightHazard),
            a: fx.highlightHazardAlpha,
          };
        case "blocked":
        default:
          return {
            c: hexToColor(colors.highlightBlocked),
            a: fx.highlightBlockedAlpha,
          };
      }
    };
    const { c, a } = colorOf(kind);
    const x = originX + col * cellW;
    const y = originY + row * cellH;
    g.fillStyle(c, a);
    g.fillRect(x + 1, y + 1, cellW - 2, cellH - 2);
  }

  sync(state: DropfrontState, dt: number): void {
    this.hitBudget = 10;
    this.shotBudget = 12;
    this.trailBudget.left =
      dropfrontCatalog.view.rig.trailBudgetPerFrame ?? 8;
    this.tracerGfx.clear();
    this.synergyGfx.clear();
    this.drawPendingAttacks(state);

    const seen = new Set<string>();
    for (const s of state.structures) {
      if (!s.alive) continue;
      seen.add(s.id);
      this.upsertStructure(s, dt);
    }
    for (const b of state.barriers) {
      if (b.hp <= 0) continue;
      seen.add(b.id);
      this.upsertBarrier(b, dt);
    }
    for (const e of state.enemies) {
      if (e.hp <= 0) continue;
      seen.add(e.id);
      this.upsertEnemy(e, dt);
    }
    for (const a of state.allies) {
      if (a.hp <= 0) continue;
      seen.add(a.id);
      this.upsertAlly(a, dt);
    }

    for (const [id, view] of [...this.views.entries()]) {
      if (view.dying) {
        view.animTime += dt;
        if (view.animTime >= view.deathDuration) {
          view.root.destroy(true);
          this.views.delete(id);
        }
        continue;
      }
      if (!seen.has(id)) {
        view.root.destroy(true);
        this.views.delete(id);
      }
    }
  }

  applyEvents(events: SimEvent[]): void {
    for (const e of events) {
      switch (e.type) {
        case DfEvent.Shot: {
          if (this.shotBudget-- <= 0) break;
          const a = this.worldToScreen(e.x0, e.y0);
          const b = this.worldToScreen(e.x1, e.y1);
          const angle = Math.atan2(b.y - a.y, b.x - a.x);
          const fx = resolveAttackFx(dropfrontCatalog.view.attackFx, e.role);
          this.vfx.play(fx.originPreset, {
            x: a.x,
            y: a.y,
            angle,
          });
          if (e.delivery === "bolt") {
            this.vfx.play(fx.flightPreset, {
              x: a.x,
              y: a.y,
              angle,
            });
          } else if (e.delivery === "pulse") {
            // Arc slash: flight = synced wedge tracer only (no sword-length streak clone).
            const mid = {
              x: (a.x + b.x) * 0.5,
              y: (a.y + b.y) * 0.5,
            };
            this.vfx.play(fx.flightPreset, {
              x: mid.x,
              y: mid.y,
              angle,
            });
          }
          break;
        }
        case DfEvent.Hit: {
          if (this.hitBudget-- <= 0) break;
          const p = this.worldToScreen(e.x, e.y);
          const victim = this.views.get(e.id);
          const role = e.role ?? "assault";
          const fx = resolveAttackFx(dropfrontCatalog.view.attackFx, role);
          const hitPreset = resolveHitPreset(fx, e.targetKind);
          let angle = 0;
          const worldAng = hitImpactAngle(e);
          if (worldAng != null) {
            const a = this.worldToScreen(e.x0!, e.y0!);
            const b = this.worldToScreen(e.x1!, e.y1!);
            angle = Math.atan2(b.y - a.y, b.x - a.x);
          }
          this.vfx.play(hitPreset, {
            x: p.x,
            y: p.y,
            angle,
            targets: {
              victim: victim?.root,
              victimBody: victim?.body,
              // Knock the whole UnitRig so weapon stays glued to body.
              victimRig: victim?.rig?.container ?? victim?.body,
            },
            vars: { damage: e.damage },
          });
          break;
        }
        case DfEvent.Land: {
          const p = this.worldToScreen(e.x, e.y);
          this.vfx.play("df.land", { x: p.x, y: p.y });
          const landed = this.views.get(e.id);
          if (landed) {
            const s = landed.baseSize;
            landed.root.setAlpha(1);
            this.setUnitChrome(landed, true);
            this.scene.tweens.add({
              targets: landed.body,
              displayWidth: s * 1.18,
              displayHeight: s * 0.82,
              duration: 90,
              yoyo: true,
            });
          }
          break;
        }
        case DfEvent.Destroy: {
          const p = this.worldToScreen(e.x, e.y);
          this.vfx.play("df.destroy", { x: p.x, y: p.y });
          this.markDying(e.id);
          break;
        }
        default:
          break;
      }
    }
  }

  private drawPendingAttacks(state: DropfrontState): void {
    const g = this.tracerGfx;
    const cell = Math.min(this.metrics.cellW, this.metrics.cellH);
    for (const atk of state.attacks) {
      this.drawAttackFlight(g, atk, cell);
    }
  }

  private drawAttackFlight(
    g: Phaser.GameObjects.Graphics,
    atk: PendingAttack,
    cell: number,
  ): void {
    const fx = resolveAttackFx(dropfrontCatalog.view.attackFx, atk.role);
    const t = attackProgress(atk.remain, atk.duration);
    const a0 = this.worldToScreen(atk.x0, atk.y0);
    const a1 = this.worldToScreen(atk.x1, atk.y1);
    const angle = Math.atan2(a1.y - a0.y, a1.x - a0.x);
    const color =
      atk.role === "swarm"
        ? 0xe23d4a
        : atk.role === "engineer"
          ? 0xffd24a
          : atk.role === "shooter"
            ? 0x7aecff
            : 0x3ecfbf;
    const alpha = 0.4 + 0.45 * (1 - t);

    if (atk.delivery === "bolt") {
      const p = lerp2(a0.x, a0.y, a1.x, a1.y, t);
      const coreR = Math.max(3.5, cell * 0.1);
      // Soft trail
      for (let i = 3; i >= 1; i--) {
        const u = t - i * 0.06;
        if (u < 0) continue;
        const tp = lerp2(a0.x, a0.y, a1.x, a1.y, u);
        g.fillStyle(color, alpha * (0.15 * i));
        g.fillCircle(tp.x, tp.y, coreR * (0.45 + 0.15 * i));
      }
      g.fillStyle(0xffffff, Math.min(1, alpha + 0.35));
      g.fillCircle(p.x, p.y, coreR * 0.55);
      g.fillStyle(color, Math.min(1, alpha + 0.25));
      g.fillCircle(p.x, p.y, coreR);
      return;
    }

    if (atk.delivery === "pulse") {
      const mid = { x: (a0.x + a1.x) * 0.5, y: (a0.y + a1.y) * 0.5 };
      const r = cell * (fx.volumeRadius ?? 0.7) * (0.4 + 0.6 * t);
      g.lineStyle(3.5, color, alpha);
      g.strokeCircle(mid.x, mid.y, r);
      g.lineStyle(1.5, 0xffffff, alpha * 0.45);
      g.strokeCircle(mid.x, mid.y, r * 0.72);
      return;
    }

    // arc / slash — filled wedge + tip glow (not a hairline path)
    const reach = cell * (fx.volumeRadius ?? 0.55) * (0.5 + 0.5 * t);
    const half = 0.5;
    const buildWedge = () => {
      g.beginPath();
      g.moveTo(a0.x, a0.y);
      for (let i = 0; i <= 8; i++) {
        const u = i / 8;
        const ang = angle - half + u * half * 2;
        g.lineTo(a0.x + Math.cos(ang) * reach, a0.y + Math.sin(ang) * reach);
      }
      g.closePath();
    };
    g.fillStyle(color, alpha * 0.22);
    buildWedge();
    g.fillPath();
    g.lineStyle(2.5, color, alpha * 0.85);
    buildWedge();
    g.strokePath();
    const tip = {
      x: a0.x + Math.cos(angle) * reach,
      y: a0.y + Math.sin(angle) * reach,
    };
    g.fillStyle(0xffffff, alpha * 0.7);
    g.fillCircle(tip.x, tip.y, Math.max(2.5, cell * 0.06));
  }

  /** Synergy wash removed — adjacency still computed in rules. */
  private drawSynergyGlow(_state: DropfrontState): void {
    this.synergyGfx.clear();
  }

  private markDying(id: string): void {
    const view = this.views.get(id);
    if (!view || view.dying) return;
    const fx = dropfrontCatalog.view.fx;
    view.dying = true;
    view.deathDuration = Math.max(0.08, fx.destroySec);
    view.animState = "death";
    view.animTime = 0;
    this.setUnitChrome(view, false);
    view.rig?.setPartsVisible(false);
    view.body.setVisible(true);
    // Keep current body frame; short burst+fade instead of death clip scrub.
    const s = view.baseSize;
    const scale = fx.destroyScale;
    const ms = view.deathDuration * 1000;
    view.root.setAlpha(1);
    view.body.setAlpha(1);
    this.scene.tweens.add({
      targets: view.body,
      displayWidth: s * scale,
      displayHeight: s * scale,
      alpha: 0,
      duration: ms,
      ease: "Cubic.easeOut",
    });
    this.scene.tweens.add({
      targets: view.root,
      alpha: 0,
      duration: ms,
      ease: "Cubic.easeIn",
    });
  }

  /** Sim SSOT: attack > walk > idle (death handled separately). */
  private desiredAnim(
    attackRemain: number,
    moveT: number,
    canWalk: boolean,
  ): AnimState {
    if (attackRemain > 0) return "attack";
    if (canWalk && moveT < 1) return "walk";
    return "idle";
  }

  private syncAnimFromSim(view: EntityView, desired: AnimState, dt: number): void {
    if (view.dying) {
      view.animTime += dt;
      return;
    }
    if (view.animState !== desired) {
      view.animState = desired;
      view.animTime = 0;
    } else {
      view.animTime += dt;
    }
  }

  /** Living combat: body clip by facing × animState × time; weapon/hand from UnitRig. */
  private applyCombatBody(view: EntityView): void {
    const facing = facingDirFromAim(view.aimAngle);
    const state: AnimState = view.dying ? "death" : view.animState;
    const { key } = ArtPipeline.ensureAnimFrame(
      this.scene,
      ART_PACK,
      view.spriteId,
      view.affiliation,
      state,
      view.animTime,
      facing,
    );
    if (key !== view.lastKey) {
      view.body.setTexture(key);
      view.body.setDisplaySize(view.baseSize, view.baseSize);
      view.lastKey = key;
      view.rig?.setBodyTexture(key);
    }
  }

  /** Props / death (no rig): state clip on body. */
  private applyBodyFrame(view: EntityView, state: AnimState): void {
    const { key } = ArtPipeline.ensureAnimFrame(
      this.scene,
      ART_PACK,
      view.spriteId,
      view.affiliation,
      state,
      view.animTime,
    );
    if (key !== view.lastKey) {
      view.body.setTexture(key);
      view.body.setDisplaySize(view.baseSize, view.baseSize);
      view.lastKey = key;
    }
  }

  private updateCombatRig(view: EntityView): void {
    if (!view.rig) return;
    this.applyCombatBody(view);
    view.rig.update(
      {
        aimAngle: view.aimAngle,
        animState: view.animState,
        animTime: view.animTime,
        intentVx: 0,
        intentVy: 0,
        facing: view.facing,
        dying: view.dying,
        // Cardinal body textures include W=flip(E); do not flipX again.
        lockBodyFlip: true,
        facingDir: facingDirFromAim(view.aimAngle),
      },
      this.trailBudget,
      this.scene,
      { x: view.root.x, y: view.root.y },
    );
  }

  private upsertAlly(a: AllyPiece, dt: number): void {
    const card = dropfrontCatalog.cardById[a.cardId];
    const spriteId = a.landing > 0 ? "pod" : card.spriteId;
    const aff = a.landing > 0 ? "pod" : "ally";
    const view = this.ensureView(a.id, spriteId, aff, 0.78);
    const w = unitWorld(a);
    const p = this.worldToScreen(w.x, w.y);
    const arc = dropfrontCatalog.view.layout.landingArcCells;
    const landT = Math.max(0.01, dropfrontCatalog.level.dropSeconds);

    view.aimAngle = a.aimAngle;
    view.facing = facingFromAim(a.aimAngle);
    view.bobPhase += dt;

    if (a.landing > 0) {
      // landing counts down landT→0: fall from high → cell (was inverted = rose up).
      const u = 1 - a.landing / landT;
      const fall = u * u; // ease-in: hang high, then accelerate
      const height = (1 - fall) * this.metrics.cellH * arc;
      view.root.setPosition(p.x, p.y - height);
      view.root.setAlpha(dropfrontCatalog.view.fx.landingAlpha);
      this.setUnitChrome(view, false);
      this.syncAnimFromSim(view, "idle", dt);
      this.applyBodyFrame(view, "idle");
      const podScale = 1 + (1 - fall) * 0.35;
      view.body.setDisplaySize(view.baseSize * podScale, view.baseSize * podScale);
    } else {
      const desired = this.desiredAnim(a.attackRemain, a.moveT, card.canMove);
      view.root.setPosition(p.x, p.y);
      view.root.setAlpha(1);
      this.setUnitChrome(view, true);
      this.syncAnimFromSim(view, desired, dt);
      if (view.rig) this.updateCombatRig(view);
      else this.applyBodyFrame(view, desired);
      view.body.setDisplaySize(view.baseSize, view.baseSize);
      this.setStackBadge(view, a.stackCount);
      this.updateHp(
        view,
        a.hp / Math.max(1, a.maxHpPerUnit * a.stackCount),
        hexToColor(dropfrontCatalog.view.colors.hpBar),
      );
    }
  }

  private upsertEnemy(e: EnemyAgent, dt: number): void {
    const scale = e.role === "brute" ? 0.85 : e.role === "runner" ? 0.55 : 0.65;
    const view = this.ensureView(e.id, "swarm", "enemy", scale);
    const w = unitWorld(e);
    const p = this.worldToScreen(w.x, w.y);
    view.root.setPosition(p.x, p.y);
    view.aimAngle = e.aimAngle;
    view.facing = facingFromAim(e.aimAngle);

    const desired = this.desiredAnim(e.attackRemain, e.moveT, true);
    this.syncAnimFromSim(view, desired, dt);
    if (view.rig) this.updateCombatRig(view);
    else this.applyBodyFrame(view, desired);
    this.setStackBadge(view, e.stackCount);
    this.updateHp(
      view,
      e.hp / Math.max(1, e.maxHpPerUnit * e.stackCount),
      hexToColor(dropfrontCatalog.view.colors.token.swarm),
    );
  }

  private upsertStructure(s: StructureToken, dt: number): void {
    const aff =
      s.kind === "hq"
        ? "objective"
        : s.kind === "depot"
          ? "supply"
          : s.kind === "nest" || s.kind === "turret"
            ? "enemy"
            : "pod";
    const spriteId =
      s.kind === "nest"
        ? "nest"
        : s.kind === "beacon"
          ? "beacon"
          : s.kind === "turret"
            ? "turret"
            : s.kind;
    const scale = s.kind === "hq" ? 1.05 : s.kind === "turret" ? 0.8 : 0.85;
    const view = this.ensureView(s.id, spriteId, aff, scale);
    const p = this.cellToScreen(s.col, s.row);
    view.bobPhase += dt;
    const pulse =
      s.kind === "nest" ||
      s.kind === "turret" ||
      (s.kind === "beacon" && s.active)
        ? Math.sin(view.bobPhase * 3) * 1.2
        : 0;
    view.root.setPosition(p.x, p.y + pulse);
    if (s.kind === "beacon" && s.active) view.root.setAlpha(1);
    this.syncAnimFromSim(view, "idle", dt);
    this.applyBodyFrame(view, "idle");
    this.setStackBadge(view, 1);
    this.updateHp(
      view,
      s.hp / s.maxHp,
      hexToColor(
        dropfrontCatalog.view.colors.token[s.kind] ??
          dropfrontCatalog.view.colors.token.ally,
      ),
    );
  }

  private upsertBarrier(
    b: { id: string; col: number; row: number; hp: number; maxHp: number },
    dt: number,
  ): void {
    const view = this.ensureView(b.id, "barrier", "neutral", 0.7);
    const p = this.cellToScreen(b.col, b.row);
    view.root.setPosition(p.x, p.y);
    view.root.setAlpha(0.55 + 0.45 * (b.hp / Math.max(1, b.maxHp)));
    this.syncAnimFromSim(view, "idle", dt);
    this.applyBodyFrame(view, "idle");
    this.setStackBadge(view, 1);
    this.updateHp(
      view,
      b.hp / Math.max(1, b.maxHp),
      hexToColor(dropfrontCatalog.view.colors.token.barrier),
    );
  }

  private ensureView(
    id: string,
    spriteId: string,
    affiliation: string,
    scaleMul: number,
  ): EntityView {
    let view = this.views.get(id);
    let keepAim = 0;
    let keepFacing: 1 | -1 = 1;
    const wantRig = usesCombatRig(spriteId);
    if (
      view &&
      (view.spriteId !== spriteId ||
        view.affiliation !== affiliation ||
        Math.abs(view.scaleMul - scaleMul) > 0.01 ||
        Boolean(view.rig) !== wantRig)
    ) {
      keepAim = view.aimAngle;
      keepFacing = view.facing;
      if (view.dying) return view;
      view.root.destroy(true);
      this.views.delete(id);
      view = undefined;
    }
    if (view) return view;

    const size = Math.min(this.metrics.cellW, this.metrics.cellH) * scaleMul;
    const root = this.scene.add.container(0, 0).setDepth(10);

    const { key } = ArtPipeline.ensureAnimFrame(
      this.scene,
      ART_PACK,
      spriteId,
      affiliation,
      "idle",
      0,
    );
    const body = this.scene.add.image(0, 0, key).setDisplaySize(size, size);

    let rig: UnitRig | null = null;
    if (wantRig) {
      rig = new UnitRig(
        this.scene,
        body,
        spriteId,
        dropfrontCatalog.view.rig,
        WALK_ENTER,
        undefined,
        dropfrontGripPack,
      );
      root.add(rig.container);
    } else {
      root.add(body);
    }

    const barW = size * 0.9;
    const barH = dropfrontCatalog.view.layout.hpBarHeight;
    const { bg: hpBg, ghost: hpGhost, fill: hpFill } = createHpBar(this.scene, {
      barW,
      barH,
      y: size * 0.42,
      bgColor: hexToColor(dropfrontCatalog.view.colors.hpBarBg),
      fillColor: hexToColor(dropfrontCatalog.view.colors.hpBar),
      bgAlpha: 0.75,
    });
    const fontSize = Math.max(
      dropfrontCatalog.view.layout.labelFontMin,
      Math.floor(size * dropfrontCatalog.view.layout.labelFontCellRatio),
    );
    const stackText = this.scene.add
      .text(0, size * 0.42, "", {
        fontFamily: "monospace",
        fontSize: `${Math.max(8, fontSize - 2)}px`,
        color: "#ffffff",
        stroke: "#000000",
        strokeThickness: 3,
      })
      .setOrigin(0.5, 0.5)
      .setVisible(false);
    root.add([hpBg, hpGhost, hpFill, stackText]);

    view = {
      root,
      body,
      rig,
      hpBg,
      hpGhost,
      hpFill,
      stackText,
      spriteId,
      affiliation,
      scaleMul,
      baseSize: size,
      lastKey: key,
      animState: "idle",
      animTime: 0,
      facing: keepFacing,
      aimAngle: keepAim,
      dying: false,
      deathDuration: 0.45,
      bobPhase: Math.random() * Math.PI * 2,
    };
    this.views.set(id, view);
    return view;
  }

  private setUnitChrome(view: EntityView, visible: boolean): void {
    view.hpBg.setVisible(visible);
    view.hpGhost.setVisible(false);
    view.hpFill.setVisible(visible);
    if (!visible) view.stackText.setVisible(false);
  }

  private setStackBadge(view: EntityView, count: number): void {
    if (count >= 1) {
      view.stackText.setText(`${count}`);
      view.stackText.setVisible(true);
    } else {
      view.stackText.setVisible(false);
    }
  }

  private updateHp(view: EntityView, ratio: number, color: number): void {
    updateHpBar(
      { bg: view.hpBg, ghost: view.hpGhost, fill: view.hpFill },
      {
        ratio: Phaser.Math.Clamp(ratio, 0, 1),
        visible: view.hpBg.visible,
        fillColor: color,
      },
    );
  }
}
